import React from 'react';
import Bugsnag from '@bugsnag/js';
import styled from 'styled-components';
import DesignerResultsGrid from '@99designs/apps/spa/pages/create/components/DesignerResultsGrid';
import DesignerResultsPagination from '@99designs/apps/spa/pages/create/components/DesignerResultsPagination';
import {
    InviteDesignersPage_StructuredBriefFragment,
    useRelevantDesignersQuery,
} from '@99designs/apps/spa/pages/create/components/InviteDesignersPage.generated';
import { Heading } from '@99designs/emu';
import { spaces } from '@99designs/emu/settings';
import { Loading } from '@99designs/spa-common';

const Centered = styled.div`
    margin: ${spaces['2bu']};
    text-align: center;
`;

interface Props {
    brief: InviteDesignersPage_StructuredBriefFragment;
}

export const DesignersPerPage = 15;

function DesignerResults(props: Props) {
    const { brief } = props;

    const { data, loading, error, fetchMore } = useRelevantDesignersQuery({
        variables: {
            id: brief.id,
            offset: 0,
            size: DesignersPerPage,
        },
    });

    if (loading) {
        return <Loading />;
    }

    if (error) {
        Bugsnag.notify(error);
        return null;
    }

    if (!data || data.designers.total === 0) {
        Bugsnag.notify(new Error('NoMatchingDesigners'), (event) => {
            event.context = `Brief ${brief.id} does not have any matching designers`;
        });

        return (
            <Centered>
                <Heading
                    accessibilityLevel={2}
                    appearanceLevel={6}
                    color="grey700"
                    fontFamily="text"
                >
                    {__('No results')}
                </Heading>
            </Centered>
        );
    }

    const { hits, total, nextOffset } = data.designers;
    const hasMoreRecommendations = hits.length < total;

    const fetchDesigners = () => {
        fetchMore({
            variables: {
                id: brief.id,
                offset: nextOffset,
                size: DesignersPerPage,
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                if (!fetchMoreResult) {
                    return previousResult;
                }

                return {
                    ...previousResult,
                    designers: {
                        ...fetchMoreResult.designers,
                        hits: [...previousResult.designers.hits, ...fetchMoreResult.designers.hits],
                    },
                };
            },
        });
    };

    return (
        <div data-testid="designer-results">
            <DesignerResultsGrid brief={brief} designers={hits} />
            {hasMoreRecommendations && <DesignerResultsPagination fetchMore={fetchDesigners} />}
        </div>
    );
}

export default DesignerResults;
